import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMeasurements } from "../../classes/contexts/MeasurementsContext";
import { useRemoteRenderContext } from "../../classes/contexts/RemoteRenderContext";
import { useUser } from "../../classes/contexts/UserContext";
import { EventsEnum } from "../../classes/enums/EventsEnum";
import { SPINESIM_MODALITIES } from "../../classes/enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../../classes/enums/PlanningStateEnum";
import { TargetTypeEnum } from "../../classes/enums/SppEnum";
import { SurgerySidebarCategories } from "../../classes/enums/SurgicalActsEnum";
import { formatTwinCreationDate } from "../../classes/utils/DateUtils";
import { getRemoteMedicalViewState } from "../../classes/utils/RemoteMedicalViewStateUtils";
import { generateStateKey } from "../../classes/utils/RemoteRenderingUtils";
import { getGroupedThumbnails } from "../../classes/utils/Thumbnailsutils";
import ThumbnailTab from "../../components/thumbnailTab/ThumbnailTab";
import useTranslation from "../../hooks/i18n/useTranslation";
import { usePatientInstancesByModality } from "../../hooks/queries/usePatientInstancesByModality";
import useCheckDataFusion from "../../hooks/useCheckDataFusion";
import useCheckSurgeryLoader from "../../hooks/useCheckSurgeryLoader";
import useCheckViewManagement from "../../hooks/useCheckViewManagement";
import UseDataFusionActions from "../../hooks/useDataFusionActions";
import { useDataBundle } from "../../hooks/useDigitalTwinDataBundle";
import useMeasurementsActions from "../../hooks/useMeasurementsActions";
import useRadioDensityManager from "../../hooks/useRadioDensityManager";
import { useRouteState } from "../../hooks/useRouteState";
import { useSppActions } from "../../hooks/useSppActions";
import "../common/stepInteractionRegion/VisualizationArea.css";
import "./Check.css";
import CheckSidebar from "./checkSidebar/CheckSidebar";
import CheckToolbar from "./checkToolbar/CheckToolbar";
import ModalityGrid from "./modalityGrid/ModalityGrid";

const Check = () => {
  const { t } = useTranslation();
  const {
    dataBundle,
    constructedImageData,
    instances,
    selectedTwin,
    measurementRefresh,
    validationRefresh,
    areDxInstancesValidated,
    areDrrInstancesValidated,
    checkReloadToken,
    refreshDigitalTwins,
  } = useDataBundle();

  const {
    client: renderClient,
    digitalTwinSurgeryCache,
    surgerySessionVersions,
    isConnected: isRemoteRenderConnected,
    isLoading: isRemoteRenderLoading,
  } = useRemoteRenderContext();
  const { dateSettings } = useUser();

  const { instancesByModality } = usePatientInstancesByModality();
  const routeState = useRouteState();
  const { navigateToValidation } = routeState;
  const selectedTwinData = selectedTwin?.[0] || null;

  // UI State
  const [activeSidebar, setActiveSidebar] = useState(
    SurgerySidebarCategories.spp
  );
  const [selectedTargetType, setSelectedTargetType] = useState(
    TargetTypeEnum.GAP
  );
  const [selectedModality, setSelectedModality] = useState(null);
  const [is3dViewActive, setIs3dViewActive] = useState(false);

  const twinId = selectedTwinData?._id || "";
  const checkSessionVersion =
    surgerySessionVersions?.[PLANNING_STATES.CHECK] ?? 0;

  const remoteKey = useMemo(() => {
    return twinId
      ? generateStateKey(PLANNING_STATES.CHECK, twinId)
      : PLANNING_STATES.CHECK;
  }, [twinId]);

  // Centralized measurement syncing for Check module
  const { originalMeasurements, setOriginalMeasurements } = useMeasurements();
  const { syncMeasurementsWithInstances } = useMeasurementsActions();

  const lastMeasurementSignatureRef = useRef(null);
  const isSyncingRef = useRef(false);

  // Computed values (needed before hooks)
  const groupedThumbnailsStandard = useMemo(() => {
    if (!instances || !instancesByModality || !selectedTwinData) return [];

    // Pass true to include Volume modalities
    return (
      getGroupedThumbnails(
        selectedTwinData,
        instancesByModality,
        null,
        null,
        true
      ) || []
    );
  }, [instances, instancesByModality, selectedTwinData]);

  const visibleThumbnails = useMemo(() => {
    if (!groupedThumbnailsStandard) return [];

    // Filter out volume modalities from thumbnail display
    return groupedThumbnailsStandard.filter(
      item =>
        item.modality !== SPINESIM_MODALITIES.CT_VOLUME &&
        item.modality !== SPINESIM_MODALITIES.MR_VOLUME
    );
  }, [groupedThumbnailsStandard]);

  const allInstances = useMemo(() => {
    if (!groupedThumbnailsStandard) return [];

    const instancesList = [];
    const instancesSet = new Set();

    groupedThumbnailsStandard.forEach(modalityGroup => {
      modalityGroup.instances.forEach(instance => {
        const instanceId = instance._id || instance.id;
        if (!instancesSet.has(instanceId)) {
          instancesSet.add(instanceId);
          instancesList.push(instance);
        }
      });
    });

    return instancesList;
  }, [groupedThumbnailsStandard]);

  const selectedTwinName = useMemo(() => {
    if (!selectedTwinData) return "";
    return selectedTwinData.title || "";
  }, [selectedTwinData]);

  const digitalTwinId = useMemo(() => {
    if (!selectedTwinData) return null;
    return selectedTwinData._id || null;
  }, [selectedTwinData]);

  const initialKeyParameters = useMemo(() => {
    if (!selectedTwinData) {
      return [];
    }

    const keyParams = selectedTwinData.key_parameters || [];
    const numericKeyParams = keyParams.map(param =>
      typeof param === "string" ? Number(param) : param
    );
    return numericKeyParams;
  }, [selectedTwinData]);

  // Check if digital twin has CT/MRI instances linked (for tool capabilities)
  const hasTwinCtInstance = useMemo(() => {
    return !!instances?.ct_instance_id;
  }, [instances]);

  const hasTwinMriInstance = useMemo(() => {
    return !!instances?.mri_instance_id;
  }, [instances]);

  const hasTwinDxInstance = useMemo(() => {
    return !!(instances?.sagittal_dx_instance_id || instances?.coronal_dx_instance_id);
  }, [instances]);

  const isFusionApplicable = useMemo(() => {
    return hasTwinCtInstance && hasTwinDxInstance;
  }, [hasTwinCtInstance, hasTwinDxInstance]);

  // For 3D View: needs either CT or MRI linked to the twin
  const hasTwin3dCapability = useMemo(() => {
    return hasTwinCtInstance || hasTwinMriInstance;
  }, [hasTwinCtInstance, hasTwinMriInstance]);

  const isCurrentModalityCtOrMr = useMemo(() => {
    return (
      selectedModality === SPINESIM_MODALITIES.CT ||
      selectedModality === SPINESIM_MODALITIES.MR ||
      selectedModality === SPINESIM_MODALITIES.CT_VOLUME ||
      selectedModality === SPINESIM_MODALITIES.MR_VOLUME
    );
  }, [selectedModality]);

  const isCurrentModalityCt = useMemo(() => {
    return (
      selectedModality === SPINESIM_MODALITIES.CT ||
      selectedModality === SPINESIM_MODALITIES.CT_VOLUME
    );
  }, [selectedModality]);

  // Custom hooks for modular functionality (called early to get areViewsReady)
  const { isLoading, areViewsReady } = useCheckSurgeryLoader({
    constructedImageData,
    renderClient,
    allInstances,
    instances,
    digitalTwinSurgeryCache,
    twinId,
    remoteKey,
    checkReloadToken,
  });

  const {
    addCustomMeasurement,
    onSppSelected,
    onSppSelectedCustom,
    activeParameters,
    removeCustomParameter,
  } = useSppActions(areViewsReady, measurementRefresh, remoteKey);

  const {
    showCtFusionView,
    hideCtFusionView,
    changeCtFusionSlice,
    getFusionVisibilityState,
  } = UseDataFusionActions(remoteKey);

  const resolveRepresentationValue = useCallback(representation => {
    if (!representation || representation.value === undefined) {
      return undefined;
    }

    const { value, type } = representation;

    // Numeric representations should be treated as finite numbers.
    if (
      type === "angle" ||
      type === "distance" ||
      type === "distance_H" ||
      type === "index" ||
      type === "score" ||
      type === "modifier"
    ) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : undefined;
    }

    // Category/classification values are preserved as-is (e.g. "L", "D", "N").
    return value;
  }, []);

  const extractMeasurementValues = useCallback(data => {
    const values = {};
    const ingestAnnotations = annotations => {
      Object.values(annotations || {}).forEach(annotation => {
        const resolvedValue = resolveRepresentationValue(
          annotation.representation
        );

        if (resolvedValue === undefined) {
          return;
        }

        const uniqueId = Number(annotation.unique_identifier);
        if (Number.isFinite(uniqueId)) {
          values[uniqueId] = resolvedValue;
        }
      });
    };

    if (data?.lateral?.annotations) {
      ingestAnnotations(data.lateral.annotations);
    }

    if (data?.frontal?.annotations) {
      ingestAnnotations(data.frontal.annotations);
    }

    return values;
  }, [resolveRepresentationValue]);

  const handleMeasurementsEvent = useCallback(
    async event => {
      try {
        const newMeasurements = event.detail;
        if (!newMeasurements?.[0]?.original?.data) {
          return;
        }

        const measurementData = newMeasurements[0].original.data;

        // Create signature to detect duplicates
        const measurementSignature = JSON.stringify({
          lateral: measurementData.lateral?.annotations,
          frontal: measurementData.frontal?.annotations,
        });

        if (lastMeasurementSignatureRef.current === measurementSignature) {
          return;
        }

        lastMeasurementSignatureRef.current = measurementSignature;

        // Update state for UI
        const extractedValues = extractMeasurementValues(measurementData);
        setOriginalMeasurements(extractedValues);

        // Sync to database (centralized, independent of sidebar)
        if (
          instances &&
          Object.keys(instances).length > 0 &&
          !isSyncingRef.current
        ) {
          isSyncingRef.current = true;
          try {
            await syncMeasurementsWithInstances(instances, measurementData);
          } catch (error) {
          } finally {
            isSyncingRef.current = false;
          }
        } else if (!instances || Object.keys(instances).length === 0) {
        }
      } catch (error) {}
    },
    [
      instances,
      extractMeasurementValues,
      setOriginalMeasurements,
      syncMeasurementsWithInstances,
    ]
  );

  // Subscribe to measurement events (independent of sidebar)
  useEffect(() => {
    window.addEventListener(
      EventsEnum.GET_MEASUREMENTS,
      handleMeasurementsEvent
    );

    return () => {
      window.removeEventListener(
        EventsEnum.GET_MEASUREMENTS,
        handleMeasurementsEvent
      );
    };
  }, [handleMeasurementsEvent]);

  // Initialize view management hook
  const viewManagement = useCheckViewManagement({
    selectedTwin,
    selectedTwinData,
    selectedModality,
    setSelectedModality,
    areViewsReady,
    groupedThumbnailsStandard,
    twinId,
    digitalTwinSurgeryCache,
    checkSessionVersion,
    instancesByModality,
  });

  const radioDensity = useRadioDensityManager({
    areViewsReady,
    renderClient,
    instances,
    dataBundle,
    remoteKey,
    areDrrInstancesValidated,
    navigateToValidation,
    routeState,
    groupedThumbnailsStandard,
    handleSelectModality: viewManagement.handleSelectModality,
    digitalTwinId,
  });

  const dataFusion = useCheckDataFusion({
    areViewsReady,
    renderClient,
    getFusionVisibilityState,
    showCtFusionView,
    hideCtFusionView,
    handleSelectModality: viewManagement.handleSelectModality,
    isFusionApplicable,
  });

  const controlsDisabled =
    isLoading || dataFusion.isDataFusionLoading || !areViewsReady;
  const remoteMedicalViewState = getRemoteMedicalViewState({
    modality: selectedModality,
    views: viewManagement.filteredViews,
    isLocalLoading: isLoading || dataFusion.isDataFusionLoading || !areViewsReady,
    isRemoteRenderConnected,
    isRemoteRenderLoading,
  });
  const remoteMedicalLoadingMessage = remoteMedicalViewState.isConnecting
    ? t("474")
    : t("475");

  // Callbacks
  const closeSidebars = useCallback(() => {
    setActiveSidebar(null);
  }, [setActiveSidebar]);

  const handleSppToggle = useCallback(() => {
    setActiveSidebar(prev =>
      prev === SurgerySidebarCategories.spp
        ? null
        : SurgerySidebarCategories.spp
    );
  }, [setActiveSidebar]);

  const handleTargetTypeChange = useCallback(newTargetType => {
    setSelectedTargetType(newTargetType);
  }, []);

  const handle3dToggle = useCallback(() => {
    viewManagement.handle3dToggle(setIs3dViewActive, groupedThumbnailsStandard);
  }, [viewManagement, groupedThumbnailsStandard]);

  // Effects
  useEffect(() => {
    if (
      instances &&
      Object.keys(instances).length > 0 &&
      dataBundle.annotations &&
      Object.keys(dataBundle.annotations).length > 0
    ) {
      // Only check DX validation when DX modality is selected
      // CT/DRR validation is only required when radiodensity is activated
      if (selectedModality === SPINESIM_MODALITIES.DX) {
        if (!areDxInstancesValidated) {
          navigateToValidation(
            routeState.params.patientId,
            routeState.params.twinId,
            SPINESIM_MODALITIES.DX
          );
          return;
        }
      }
    }
  }, [
    instances,
    dataBundle.annotations,
    selectedModality,
    areDxInstancesValidated,
    navigateToValidation,
    routeState.params.patientId,
    routeState.params.twinId,
  ]);

  useEffect(() => {
    if (visibleThumbnails?.length > 0 && !selectedModality) {
      const firstModality = visibleThumbnails[0].modality;
      setSelectedModality(firstModality);
    }
  }, [visibleThumbnails, selectedModality]);

  useEffect(() => {
    if (isCurrentModalityCtOrMr) {
      closeSidebars();
    }
  }, [isCurrentModalityCtOrMr, closeSidebars]);

  // Auto-deactivate radio density when switching away from CT modality
  const { autoDeactivateOnModalityChange } = radioDensity;

  useEffect(() => {
    autoDeactivateOnModalityChange(selectedModality);
  }, [selectedModality, autoDeactivateOnModalityChange]);

  useEffect(() => {
    if (
      is3dViewActive &&
      selectedModality !== SPINESIM_MODALITIES.CT_VOLUME &&
      selectedModality !== SPINESIM_MODALITIES.MR_VOLUME
    ) {
      setIs3dViewActive(false);
    }
  }, [selectedModality, is3dViewActive]);

  return (
    <div className="Visualization-region-container">
        <div className="Visualization-region-header">
          <div className="Visualization-region-title App-title-widgets">
            {`Digital Twin: ${selectedTwinName}`}
          </div>
        {selectedTwinData &&
          formatTwinCreationDate(selectedTwinData, null, {
            locale: dateSettings?.locale,
            format: dateSettings?.format,
          }) && (
            <div className="Visualization-region-date">
              {formatTwinCreationDate(selectedTwinData, null, {
                locale: dateSettings?.locale,
                format: dateSettings?.format,
              })}
            </div>
          )}
      </div>
      <div className="Visualization-region-content">
        <div className="Visualization-region-tab">
          <ThumbnailTab
            thumbnails={visibleThumbnails}
            selectedThumbnail={selectedModality}
            onSelectThumbnail={viewManagement.handleSelectModality}
          />
        </div>
        <div className="Visualization-region-views">
          <ModalityGrid
            selectedModality={selectedModality}
            isLoading={remoteMedicalViewState.isLoading}
            isRadioDensityActive={radioDensity.isRadioDensityActive}
            filteredViews={viewManagement.filteredViews}
            selectedTwin={selectedTwin}
            areViewsReady={areViewsReady}
            groupedThumbnailsStandard={groupedThumbnailsStandard}
            twinId={twinId}
            digitalTwinSurgeryCache={digitalTwinSurgeryCache}
            renderClient={renderClient}
            annotations={dataBundle.organizedAnnotations}
            isDataFusionActive={dataFusion.isDataFusionActive}
            handleImageClose={viewManagement.handleImageClose}
            changeCtFusionSlice={changeCtFusionSlice}
            addCustomMeasurement={addCustomMeasurement}
            isViewClosing={viewManagement.isViewClosing}
            onRadioDensityStatisticsUpdate={
              radioDensity.handleRadioDensityStatisticsUpdate
            }
            planId={remoteKey}
            initialVertebraDimensions={radioDensity.initialVertebraDimensions}
            loadingMessage={remoteMedicalLoadingMessage}
          />
        </div>
        <div className="Visualization-region-tools-wrapper">
          <CheckToolbar
            controlsDisabled={controlsDisabled}
            isCurrentModalityCtOrMr={isCurrentModalityCtOrMr}
            isCurrentModalityCt={isCurrentModalityCt}
            hasTwinCtInstance={hasTwinCtInstance}
            isFusionApplicable={isFusionApplicable}
            hasTwin3dCapability={hasTwin3dCapability}
            is3dViewActive={is3dViewActive}
            isRadioDensityActive={radioDensity.isRadioDensityActive}
            isDataFusionActive={dataFusion.isDataFusionActive}
            activeSidebar={activeSidebar}
            onSppToggle={handleSppToggle}
            onView3dToggle={handle3dToggle}
            onRadioDensityToggle={radioDensity.handleToggleRadioDensity}
            onDataFusionToggle={dataFusion.handleToggleDataFusion}
          />
        </div>
      </div>
      <CheckSidebar
        activeSidebar={activeSidebar}
        isRadioDensityActive={radioDensity.isRadioDensityActive}
        dataBundle={dataBundle}
        selectedModality={selectedModality}
        onSppSelected={onSppSelected}
        onSppSelectedCustom={onSppSelectedCustom}
        activeParameters={activeParameters}
        removeCustomParameter={removeCustomParameter}
        instances={instances}
        validationRefresh={validationRefresh}
        selectedTargetType={selectedTargetType}
        onTargetTypeChange={handleTargetTypeChange}
        digitalTwinId={digitalTwinId}
        initialKeyParameters={initialKeyParameters}
        originalMeasurements={originalMeasurements}
        radioDensityStatistics={radioDensity.radioDensityStatistics}
        onRadioDensityStatisticsUpdate={
          radioDensity.handleRadioDensityStatisticsUpdate
        }
        areViewsReady={areViewsReady}
      />
    </div>
  );
};

export default Check;
